import React, { useState } from 'react';
import Head from 'next/head';
import Header from '../components/Header';
import Footer from '../components/Footer';
import IntegrationsSection from '../components/IntegrationsSection';

export default function Integrations() {
  // Code sample tabs state management
  const [activeTab, setActiveTab] = React.useState('javascript');
  
  // API endpoints data
  const apiEndpoints = [
    {
      method: 'POST',
      path: '/v1/content',
      description: 'Generate a document from a template',
      parameters: [
        { name: 'TemplateId', type: 'string', required: true, description: 'ID of the Personalia template to use' },
        { name: 'Fields', type: 'object', required: true, description: 'Key-value pairs of template fields' },
        { 
          name: 'Output', 
          type: 'object', 
          required: false, 
          description: 'Output configuration (format, quality, resolution, etc.)',
          default: '{"Format": "PDF", "Quality": "Display", "Package": false}'
        },
      ],
    },
    {
      method: 'GET',
      path: '/v1/content',
      description: 'Get the status and result of a content generation request',
      parameters: [
        { name: 'requestId', type: 'string', required: true, description: 'ID of the content generation request' },
      ],
    },
    {
      method: 'POST',
      path: '/v1/content/url',
      description: 'Generate a URL that creates content when accessed',
      parameters: [
        { name: 'TemplateId', type: 'string', required: true, description: 'ID of the Personalia template to use' },
        { name: 'Fields', type: 'object', required: true, description: 'Key-value pairs of template fields' },
        { 
          name: 'Output', 
          type: 'object', 
          required: false, 
          description: 'Output configuration (only PDF format is supported for URLs)',
          default: '{"Format": "PDF", "Quality": "Display"}'
        },
      ],
    },
    {
      method: 'GET',
      path: '/v1/content/templates/{templateId}/info',
      description: 'Get information about a template including its fields',
      parameters: [
        { name: 'templateId', type: 'string', required: true, description: 'ID of the template' },
      ],
    },
  ];

  // Quick start code examples by language
  const codeExamples = {
    curl: `# Generate a document using cURL\ncurl -X POST https://api.personalia.io/v1/content \\
  -H "Authorization: ApiKey YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{\n    "TemplateId": "c790fd3b-10ef-4a58-963b-b688e564eefa",\n    "Fields": {\n      "First Name": "John",\n      "Machine": "AK-2000",\n      "Color": "Blue"\n    },\n    "Output": {\n      "Format": "PDF",\n      "Quality": "Display"\n    }\n  }'`,
    
    javascript: `// Generate a document using JavaScript\nconst response = await fetch('https://api.personalia.io/v1/content', {\n  method: 'POST',\n  headers: {\n    'Content-Type': 'application/json',\n    'Authorization': 'ApiKey YOUR_API_KEY'\n  },\n  body: JSON.stringify({\n    TemplateId: 'c790fd3b-10ef-4a58-963b-b688e564eefa',\n    Fields: {\n      'First Name': 'John',\n      'Machine': 'AK-2000',\n      'Color': 'Blue'\n    },\n    Output: {\n      Format: 'PDF',\n      Quality: 'Display'\n    }\n  })\n});\nconst { RequestId } = await response.json();`,
    
    python: `# Generate a document using Python\nimport requests\n\nurl = "https://api.personalia.io/v1/content"\nheaders = {\n    "Authorization": "ApiKey YOUR_API_KEY",\n    "Content-Type": "application/json"\n}\ndata = {\n    "TemplateId": "c790fd3b-10ef-4a58-963b-b688e564eefa",\n    "Fields": {\n        "First Name": "John",\n        "Machine": "AK-2000",\n        "Color": "Blue"\n    },\n    "Output": {\n        "Format": "PDF",\n        "Quality": "Display"\n    }\n}\n\nresponse = requests.post(url, headers=headers, json=data)\nresult = response.json()\nrequest_id = result.get('RequestId')`
  };

  return (
    <>
      <Head>
        <title>Integrations & API | Personalia - InDesign Automation Platform</title>
        <meta name="description" content="Connect Personalia with your favorite tools using our API and pre-built integrations. Automate your creative workflows with ease." />
        <script src="https://unpkg.com/react@17/umd/react.production.min.js"></script>
      </Head>

      <Header />

      <main id="main-content">
        {/* Hero Section */}
        <section className="bg-gray-900 text-white py-16 md:py-24">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Integrations & API
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Connect Personalia with your favorite tools and automate your creative workflows with our developer-friendly API.
              </p>
            </div>
          </div>
        </section>

        {/* Reuse the existing IntegrationsSection */}
        <IntegrationsSection />

        {/* API Quick Start Guide Section */}
        <section className="section bg-gray-50 py-16">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-4 text-center">Quick Start API Guide</h2>
              <p className="text-center text-lg text-gray-600 mb-10">
                Get started quickly with our API. For full documentation, visit{' '}
                <a href="https://developer.personalia.io" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">developer.personalia.io</a>
              </p>
              
              {/* Authentication */}
              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-4">Authentication</h3>
                <p className="mb-6 text-gray-700">
                  All API requests must include an <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">Authorization</code> header with your API key in the format:
                </p>
                
                <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-mono text-sm font-medium text-gray-700">Authorization: ApiKey YOUR_API_KEY</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    You can find your API key in the <a href="https://dashboard.personalia.io/api-keys" className="text-primary hover:underline">API Keys</a> section of your Personalia dashboard.
                  </p>
                </div>

                {/* Tabbed Code Examples */}
                <div className="space-y-6">
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                    {/* Tabs */}
                    <div className="flex border-b border-gray-200">
                      <button
                        className={`px-6 py-3 text-sm font-medium ${activeTab === 'javascript' ? 'text-primary border-b-2 border-primary' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => setActiveTab('javascript')}
                      >
                        JavaScript
                      </button>
                      <button
                        className={`px-6 py-3 text-sm font-medium ${activeTab === 'python' ? 'text-primary border-b-2 border-primary' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => setActiveTab('python')}
                      >
                        Python
                      </button>
                      <button
                        className={`px-6 py-3 text-sm font-medium ${activeTab === 'curl' ? 'text-primary border-b-2 border-primary' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => setActiveTab('curl')}
                      >
                        cURL
                      </button>
                    </div>
                    
                    {/* Tab Content */}
                    <div className="p-0">
                      <div className={`${activeTab === 'javascript' ? 'block' : 'hidden'}`}>
                        <pre className="bg-gray-900 text-gray-100 p-5 rounded-b-lg overflow-x-auto text-sm">
                          <code>{codeExamples.javascript}</code>
                        </pre>
                      </div>
                      <div className={`${activeTab === 'python' ? 'block' : 'hidden'}`}>
                        <pre className="bg-gray-900 text-gray-100 p-5 rounded-b-lg overflow-x-auto text-sm">
                          <code>{codeExamples.python}</code>
                        </pre>
                      </div>
                      <div className={`${activeTab === 'curl' ? 'block' : 'hidden'}`}>
                        <pre className="bg-gray-900 text-gray-100 p-5 rounded-b-lg overflow-x-auto text-sm">
                          <code>{codeExamples.curl}</code>
                        </pre>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Endpoints */}
              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">API Endpoints</h3>
                
                <div className="space-y-8">
                  {apiEndpoints.map((endpoint, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            endpoint.method === 'GET' ? 'bg-blue-100 text-blue-800' :
                            endpoint.method === 'POST' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {endpoint.method}
                          </span>
                          <span className="ml-4 font-mono text-gray-800">{endpoint.path}</span>
                        </div>
                        <p className="mt-2 text-gray-600">{endpoint.description}</p>
                      </div>
                      
                      {endpoint.parameters && (
                        <div className="bg-white p-6">
                          <h4 className="font-medium text-gray-900 mb-3">Parameters</h4>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {endpoint.parameters.map((param, i) => (
                                  <tr key={i}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{param.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{param.type}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {param.required ? (
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                          Required
                                        </span>
                                      ) : (
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                          Optional
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                      {param.description}
                                      {param.default && (
                                        <span className="text-gray-400 ml-2">Default: {param.default}</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* API Workflow */}
              <div className="my-16">
                <h3 className="text-2xl font-semibold mb-6 text-center">API Workflow</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
                  <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                    <div className="bg-primary bg-opacity-10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                      <span className="text-primary text-xl font-bold">1</span>
                    </div>
                    <h4 className="text-lg font-semibold mb-3">Create Templates</h4>
                    <p className="text-gray-600">
                      Design your templates in InDesign and upload them to Personalia through the dashboard.
                    </p>

                  </div>

                  <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                    <div className="bg-primary bg-opacity-10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                      <span className="text-primary text-xl font-bold">2</span>
                    </div>
                    <h4 className="text-lg font-semibold mb-3">Get Your API Key</h4>
                    <p className="text-gray-600">
                      Create an account and get your API key from the Personalia dashboard.
                    </p>
                  </div>

                  <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                    <div className="bg-primary bg-opacity-10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                      <span className="text-primary text-xl font-bold">3</span>
                    </div>
                    <h4 className="text-lg font-semibold mb-3">Generate Content</h4>
                    <p className="text-gray-600">
                      Use the API to generate personalized PDFs or images with data from your app, service or platform.
                    </p>
                  </div>
                </div>
              </div>

              {/* Error Handling */}
              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-4">Error Handling</h3>
                <p className="text-gray-700 mb-4">
                  The API uses standard HTTP status codes to indicate success or failure of an API request.
                </p>
                
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mt-6">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <h4 className="text-lg font-medium">Error Response Example</h4>
                  </div>
                  <div className="p-6">
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded overflow-x-auto text-sm">
                      <code>{
`{
  "error": {
    "code": "invalid_request_error",
    "message": "The request was invalid. Check the request parameters and try again.",
    "param": "templateId",
    "type": "invalid_request_error"
  }
}`
                      }</code>
                    </pre>
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-lg font-medium mb-3">Common Error Codes</h4>
                  <div className="overflow-hidden border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status Code</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">400</td>
                          <td className="px-6 py-4 text-sm text-gray-500">Bad Request - Invalid request format or parameters</td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">401</td>
                          <td className="px-6 py-4 text-sm text-gray-500">Unauthorized - Invalid or missing API key</td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">403</td>
                          <td className="px-6 py-4 text-sm text-gray-500">Forbidden - Insufficient permissions</td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">404</td>
                          <td className="px-6 py-4 text-sm text-gray-500">Not Found - Resource not found</td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">429</td>
                          <td className="px-6 py-4 text-sm text-gray-500">Too Many Requests - Rate limit exceeded</td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">5xx</td>
                          <td className="px-6 py-4 text-sm text-gray-500">Server Error - Something went wrong on our end</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
